import React, { useMemo } from 'react';
import { ReactComponent as CartIcon } from '../../../icons/svg/shopping-cart.svg';
import { Badge } from '../../../components/badge';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';
import { HoverCard, HoverCardContent } from '../../../components/hover-card';
import { HoverCardTrigger } from '../../../components/hover-card/hover-card-trigger';
import type { HeaderCartInfo } from './Header';
import { ShoppingCartMenu } from './ShoppingCartMenu';
import { useAtom } from 'jotai';
import { isShoppingCartMenuOpenState } from '../states/ui-shopping-cart-menu';
const _cartIconVariants = cva('size-5', {
    variants: {
        fill: {
            dark: '[&>path]:fill-gray-900!',
            light: '[&>path]:fill-gray-50!',
        },
    },
    defaultVariants: {
        fill: 'dark',
    },
});

interface UserCartProps extends VariantProps<typeof _cartIconVariants> {
    onUserCartClick: () => void;
    cartInfo: HeaderCartInfo;
    /**
     * Open the mini-cart when a pointer rests on the icon.
     *
     * The revamp's header opens a cart drawer on click instead, where the menu
     * is a confirmation shown after an add rather than something offered to a
     * pointer on its way somewhere else. Defaults to the hover the legacy
     * header has always had, so a caller that says nothing keeps it.
     */
    openCartMenuOnHover?: boolean;
}

export const UserCart = (props: UserCartProps) => {
    const {
        fill,
        onUserCartClick,
        cartInfo,
        openCartMenuOnHover = true,
    } = props;
    const [isShoppingCartMenuOpen, setIsShoppingCartMenuOpen] = useAtom(
        isShoppingCartMenuOpenState,
    );
    const { cartCount } = cartInfo;

    const cartIconComponent = (
        <button
            className="relative flex items-center"
            onClick={onUserCartClick}
        >
            <div className="absolute -top-2 -right-2 flex">
                {cartCount > 0 && (
                    <Badge
                        size="centeredCompact"
                        text={cartCount > 9 ? 'small' : 'default'}
                        color={fill === 'dark' ? 'default' : 'destructive'}
                    >
                        {cartCount > 9 ? '9+' : cartCount}
                    </Badge>
                )}
            </div>
            <div className="flex items-center" aria-label="Cart">
                <CartIcon
                    className={cn(_cartIconVariants({ fill }), 'size-6')}
                />
            </div>
        </button>
    );

    const shoppingCartMenu = useMemo(
        () => (
            <ShoppingCartMenu
                cartInfo={cartInfo}
                onUserCartClick={onUserCartClick}
            />
        ),
        [cartInfo, onUserCartClick],
    );

    const renderDesktopCartMenu = () => (
        <HoverCard open={isShoppingCartMenuOpen}>
            {/* Where the click opens a drawer the menu is a confirmation,
                shown after an add rather than offered to a pointer passing
                over the icon on its way somewhere else. Where the click still
                navigates to the cart, the hover is how the menu is reached. */}
            <HoverCardTrigger
                onMouseEnter={
                    openCartMenuOnHover
                        ? () => setIsShoppingCartMenuOpen(true)
                        : undefined
                }
                aria-expanded={isShoppingCartMenuOpen}
                aria-haspopup="true"
                aria-label={`Shopping cart with ${cartCount} items`}
            >
                {cartIconComponent}
            </HoverCardTrigger>
            <HoverCardContent
                variant="cart"
                onMouseLeave={() => setIsShoppingCartMenuOpen(false)}
                role="dialog"
                aria-label="Shopping cart contents"
            >
                {shoppingCartMenu}
            </HoverCardContent>
        </HoverCard>
    );

    if (cartCount === 0) return null;

    return (
        <>
            <div className="hidden md:flex md:items-center">
                {renderDesktopCartMenu()}
            </div>
            <div className="flex items-center md:hidden">
                {cartIconComponent}
            </div>
        </>
    );
};
